import React from 'react';

const COLORS = ['#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4'];

const CoordinateGrid = ({ data }) => {
  if (!data) return null;

  const {
    xRange = [-6, 6],
    yRange = [-6, 6],
    elements = [],
    title = '',
    showLabels = true
  } = data;

  const xMin = xRange[0], xMax = xRange[1];
  const yMin = yRange[0], yMax = yRange[1];
  const w = xMax - xMin;
  const h = yMax - yMin;

  const pad = 30; // padding in SVG units
  const svgW = 300, svgH = 300;
  const plotW = svgW - pad * 2;
  const plotH = svgH - pad * 2;

  // Convert math coords to SVG coords
  const sx = (x) => pad + ((x - xMin) / w) * plotW;
  const sy = (y) => pad + ((yMax - y) / h) * plotH;

  // Grid step: use 1 for small ranges, 2 for large
  const step = Math.max(1, Math.ceil(w / 14));

  // Generate grid lines
  const gridLines = [];
  for (let x = Math.ceil(xMin / step) * step; x <= xMax; x += step) {
    gridLines.push(
      <line key={`gx${x}`} x1={sx(x)} y1={pad} x2={sx(x)} y2={svgH - pad}
        stroke={x === 0 ? '#333' : '#ddd'} strokeWidth={x === 0 ? 1.5 : 0.5} />
    );
    if (showLabels && x !== 0) {
      gridLines.push(
        <text key={`lx${x}`} x={sx(x)} y={sy(0) + 14}
          textAnchor="middle" className="diagram-grid-label">{x}</text>
      );
    }
  }
  for (let y = Math.ceil(yMin / step) * step; y <= yMax; y += step) {
    gridLines.push(
      <line key={`gy${y}`} x1={pad} y1={sy(y)} x2={svgW - pad} y2={sy(y)}
        stroke={y === 0 ? '#333' : '#ddd'} strokeWidth={y === 0 ? 1.5 : 0.5} />
    );
    if (showLabels && y !== 0) {
      gridLines.push(
        <text key={`ly${y}`} x={sx(0) - 8} y={sy(y) + 4}
          textAnchor="end" className="diagram-grid-label">{y}</text>
      );
    }
  }

  // Axis arrows
  const axes = (
    <g className="coord-axes">
      {/* X axis arrow */}
      <line x1={pad - 5} y1={sy(0)} x2={svgW - pad + 5} y2={sy(0)} stroke="#333" strokeWidth="1.5" />
      <polygon points={`${svgW - pad + 10},${sy(0)} ${svgW - pad + 2},${sy(0) - 4} ${svgW - pad + 2},${sy(0) + 4}`} fill="#333" />
      <text x={svgW - pad + 12} y={sy(0) + 4} className="diagram-axis-label">x</text>
      {/* Y axis arrow */}
      <line x1={sx(0)} y1={svgH - pad + 5} x2={sx(0)} y2={pad - 5} stroke="#333" strokeWidth="1.5" />
      <polygon points={`${sx(0)},${pad - 10} ${sx(0) - 4},${pad - 2} ${sx(0) + 4},${pad - 2}`} fill="#333" />
      <text x={sx(0) + 8} y={pad - 4} className="diagram-axis-label">y</text>
      {/* Origin */}
      {showLabels && <text x={sx(0) - 8} y={sy(0) + 14} textAnchor="end" className="diagram-grid-label">0</text>}
    </g>
  );

  // Render elements
  let colorIdx = 0;
  const getColor = (c) => c || COLORS[colorIdx++ % COLORS.length];

  const renderElement = (el, i) => {
    switch (el.type) {
      case 'point': {
        const color = getColor(el.color);
        return (
          <g key={`pt${i}`}>
            <circle cx={sx(el.x)} cy={sy(el.y)} r="4" fill={color} />
            {el.label && (
              <text x={sx(el.x) + 8} y={sy(el.y) - 8}
                className="diagram-point-label" fill={color}>
                {el.label}{el.showCoords !== false ? ` (${el.x}; ${el.y})` : ''}
              </text>
            )}
          </g>
        );
      }

      case 'line': {
        const color = getColor(el.color);
        // Line from equation y = mx + c, or from two points
        if (el.points && el.points.length >= 2) {
          const pts = el.points;
          return (
            <g key={`ln${i}`}>
              <polyline
                points={pts.map(p => `${sx(p[0])},${sy(p[1])}`).join(' ')}
                fill="none" stroke={color} strokeWidth="2"
                strokeDasharray={el.dashed ? '6,4' : 'none'}
              />
              {el.label && (
                <text x={sx(pts[Math.floor(pts.length / 2)][0]) + 8}
                  y={sy(pts[Math.floor(pts.length / 2)][1]) - 8}
                  className="diagram-line-label" fill={color}>{el.label}</text>
              )}
            </g>
          );
        }
        if (el.m !== undefined && el.c !== undefined) {
          const x1 = xMin, x2 = xMax;
          const y1 = el.m * x1 + el.c, y2 = el.m * x2 + el.c;
          return (
            <g key={`ln${i}`}>
              <line x1={sx(x1)} y1={sy(y1)} x2={sx(x2)} y2={sy(y2)}
                stroke={color} strokeWidth="2"
                strokeDasharray={el.dashed ? '6,4' : 'none'}
              />
              {el.label && (
                <text x={sx((x1 + x2) / 2) + 8} y={sy(el.m * (x1 + x2) / 2 + el.c) - 8}
                  className="diagram-line-label" fill={color}>{el.label}</text>
              )}
            </g>
          );
        }
        // Vertical line x = k
        if (el.x !== undefined) {
          return (
            <g key={`ln${i}`}>
              <line x1={sx(el.x)} y1={pad} x2={sx(el.x)} y2={svgH - pad}
                stroke={color} strokeWidth="2"
                strokeDasharray={el.dashed ? '6,4' : 'none'}
              />
              {el.label && (
                <text x={sx(el.x) + 8} y={pad + 16}
                  className="diagram-line-label" fill={color}>{el.label}</text>
              )}
            </g>
          );
        }
        return null;
      }

      case 'polygon': {
        const color = getColor(el.color);
        const verts = el.vertices || [];
        const labels = el.labels || [];
        return (
          <g key={`pg${i}`}>
            <polygon
              points={verts.map(v => `${sx(v[0])},${sy(v[1])}`).join(' ')}
              fill={el.fill || 'rgba(33,150,243,0.1)'}
              stroke={color} strokeWidth="2"
            />
            {verts.map((v, j) => (
              <g key={`pgv${j}`}>
                <circle cx={sx(v[0])} cy={sy(v[1])} r="3" fill={color} />
                {labels[j] && (
                  <text x={sx(v[0]) + 8} y={sy(v[1]) - 8}
                    className="diagram-point-label" fill={color}>
                    {labels[j]}({v[0]}; {v[1]})
                  </text>
                )}
              </g>
            ))}
          </g>
        );
      }

      case 'segment': {
        const color = getColor(el.color);
        return (
          <g key={`seg${i}`}>
            <line x1={sx(el.x1)} y1={sy(el.y1)} x2={sx(el.x2)} y2={sy(el.y2)}
              stroke={color} strokeWidth="2"
              strokeDasharray={el.dashed ? '6,4' : 'none'}
            />
            {el.label && (
              <text x={sx((el.x1 + el.x2) / 2) + 8} y={sy((el.y1 + el.y2) / 2) - 8}
                className="diagram-line-label" fill={color}>{el.label}</text>
            )}
          </g>
        );
      }

      case 'vector': {
        const color = getColor(el.color);
        const dx = el.x2 - el.x1, dy = el.y2 - el.y1;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const ux = dx / len, uy = dy / len;
        const arrowSize = 0.3;
        const tipX = el.x2, tipY = el.y2;
        const l1x = tipX - arrowSize * (ux + uy * 0.5);
        const l1y = tipY - arrowSize * (uy - ux * 0.5);
        const l2x = tipX - arrowSize * (ux - uy * 0.5);
        const l2y = tipY - arrowSize * (uy + ux * 0.5);
        return (
          <g key={`vec${i}`}>
            <line x1={sx(el.x1)} y1={sy(el.y1)} x2={sx(el.x2)} y2={sy(el.y2)}
              stroke={color} strokeWidth="2" />
            <polygon
              points={`${sx(tipX)},${sy(tipY)} ${sx(l1x)},${sy(l1y)} ${sx(l2x)},${sy(l2y)}`}
              fill={color} />
            {el.label && (
              <text x={sx((el.x1 + el.x2) / 2) + 8} y={sy((el.y1 + el.y2) / 2) - 8}
                className="diagram-line-label" fill={color}>{el.label}</text>
            )}
          </g>
        );
      }

      case 'curve': {
        const color = getColor(el.color);
        const pts = el.points || [];
        if (pts.length < 2) return null;
        const pathData = pts.map((p, j) =>
          `${j === 0 ? 'M' : 'L'}${sx(p[0])},${sy(p[1])}`
        ).join(' ');
        return (
          <g key={`crv${i}`}>
            <path d={pathData} fill="none" stroke={color} strokeWidth="2"
              strokeDasharray={el.dashed ? '6,4' : 'none'} />
            {el.label && (
              <text x={sx(pts[Math.floor(pts.length / 2)][0]) + 8}
                y={sy(pts[Math.floor(pts.length / 2)][1]) - 8}
                className="diagram-line-label" fill={color}>{el.label}</text>
            )}
          </g>
        );
      }

      case 'circle': {
        const color = getColor(el.color);
        const r = (el.r / w) * plotW;
        return (
          <g key={`crc${i}`}>
            <circle cx={sx(el.cx || 0)} cy={sy(el.cy || 0)} r={r}
              fill={el.fill || 'none'} stroke={color} strokeWidth="2" />
            {el.label && (
              <text x={sx(el.cx || 0) + r + 6} y={sy(el.cy || 0)}
                className="diagram-line-label" fill={color}>{el.label}</text>
            )}
          </g>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="diagram-container">
      {title && <div className="diagram-title">{title}</div>}
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="coordinate-svg" role="img" aria-label={title || 'Coordinate grid'}>
        {gridLines}
        {axes}
        {elements.map(renderElement)}
      </svg>
    </div>
  );
};

export default CoordinateGrid;
